import {
    CssBaseline,
    ThemeProvider as MuiThemeProvider,
    createTheme,
} from "@mui/material";
import React from "react";
import { deDE as coreDeDE } from '@mui/material/locale';
import { deDE } from '@mui/x-date-pickers/locales';

const baseTheme = createTheme();

const appTheme = createTheme(
    {
        cssVariables: true,
        palette: {
            primary: {
                main: "rgba(0, 3, 31, 1)",
            },
            secondary: {
                main: "rgba(255, 157, 0, 1)",
            },
            error: {
                main: "rgba(220, 27, 36, 1)",
            },
            warning: {
                main: "rgba(255, 137, 0, 1)",
            },
            info: {
                main: "rgba(9, 91, 255, 1)",
            },
            success: {
                main: "rgba(121, 255, 126, 1)",
            },
            background: {
                default: "rgba(245, 245, 242, 1)",
                paper: "rgba(255, 255, 255, 1)",
            },
            text: {
                primary: "rgba(29, 29, 29, 1)",
                secondary: "rgba(0, 3, 31, 1)",
            },
            grey: {
                50: "rgba(208, 208, 208, 1)",
                100: "rgba(217, 217, 217, 1)",
                200: "rgba(227, 227, 227, 1)",
                300: "rgba(26, 28, 47, 1)",
                400: "rgba(4, 27, 43, 1)",
                500: "rgba(0, 3, 31, 0.4)",
                600: "rgba(0, 3, 31, 0.66)",
            },
            fuschia: {
                100: "rgba(239, 93, 168, 1)",
                60: "rgba(252, 221, 236, 1)",
                80: "rgba(241, 120, 182, 1)",
            },
            iris: {
                100: "rgba(93, 95, 239, 1)",
                60: "rgba(165, 166, 246, 1)",
                80: "rgba(120, 121, 241, 1)",
            },
        },
        typography: {
            allVariants: {
                fontSize: 16,
            },
            fontFamily: '"Work Sans", sans-serif',
            fontSize: 16,
            h1: {
                fontFamily: '"Sora", sans-serif',
                fontSize: "2rem", // 32px
                [baseTheme.breakpoints.down('md')]: {
                    fontSize: "1.5rem", // 24px
                },
                fontWeight: 700,
            },
            h2: {
                fontFamily: '"Sora", sans-serif',
                fontSize: "1.375rem", // 22px
                fontWeight: 600,
                [baseTheme.breakpoints.down('md')]: {
                    fontSize: "1rem", // 16px
                },
            },
            h3: {
                fontFamily: '"Inter", sans-serif',
                fontSize: "1.5rem", // 24px
                [baseTheme.breakpoints.down('md')]: {
                    fontSize: "1.125rem", // 18px
                },
            },
            h4: {
                fontFamily: '"Sora", sans-serif',
                fontSize: "1.75rem", // 28px
                fontWeight: "bold",
                letterSpacing: "0",
                [baseTheme.breakpoints.down('md')]: {
                    fontSize: "1.3125rem", // 21px
                },
            },
            h5: {
                fontFamily: '"Sora", sans-serif',
                fontSize: "1.375rem", // 22px
                fontWeight: 600,
                letterSpacing: "0",
                [baseTheme.breakpoints.down('md')]: {
                    fontSize: "1rem", // 18px
                },
            },
            h6: {
                fontFamily: '"Inter", sans-serif',
                fontSize: "1.125rem", // 18px
                fontWeight: 400,
                letterSpacing: "0",
                [baseTheme.breakpoints.down('md')]: {
                    fontSize: "0,875rem", // 14px
                },
            },
            subtitle1: {
                fontFamily: '"Sora", sans-serif',
                fontWeight: 600,
                letterSpacing: "0",
                [baseTheme.breakpoints.down('md')]: {
                    fontSize: 14,
                },
            },
            subtitle2: {
                fontFamily: '"Inter", sans-serif',
                fontSize: "1.5rem",
                fontWeight: 400,
                letterSpacing: "0",
            },
            body1: {
                fontFamily: '"Sora", sans-serif',
                [baseTheme.breakpoints.down('md')]: {
                    fontSize: 14,
                },
            },
            body2: {
                fontFamily: '"Sora", sans-serif',
                fontWeight: 400,
                letterSpacing: "0",
                [baseTheme.breakpoints.down('md')]: {
                    fontSize: 14,
                },
            },
            caption: {
                fontFamily: '"Inter", sans-serif',
                fontWeight: 400,
                letterSpacing: "0",
            },
            overline: {
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontWeight: 400,
                letterSpacing: "0",
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        textTransform: "none",
                        ...theme.typography.body2,
                    })
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        ...theme.typography.body2,
                    }),
                    head: ({ theme }) => ({
                        ...theme.typography.subtitle1,
                    }),
                    body: ({ theme }) => ({
                        ...theme.typography.body1,
                    }),
                },
            },
            MuiListItemText: {
                styleOverrides: {
                    primary: ({ theme }) => ({
                        ...theme.typography.h5,
                    }),
                    secondary: ({ theme }) => ({
                        ...theme.typography.body2,
                    }),
                },
            },
        },
    },
    deDE, // x-date-pickers translations
    coreDeDE
); // core translations

declare module "@mui/material/styles" {
    interface Palette {
        fuschia: {
            100: string;
            60: string;
            80: string;
        };
        iris: {
            100: string;
            60: string;
            80: string;
        };
    }

    interface PaletteOptions {
        fuschia?: {
            100?: string;
            60?: string;
            80?: string;
        };
        iris?: {
            100?: string;
            60?: string;
            80?: string;
        };
    }
}

const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
    // appTheme = responsiveFontSizes(appTheme, {
    //     disableAlign: true,
    // });

    return (
        <MuiThemeProvider theme={appTheme}>
            <CssBaseline />
            {children}
        </MuiThemeProvider>
    );
};

export default ThemeProvider;